import { useState } from 'react'
import {
  Box, Card, CardContent, Typography, Collapse, Button, Grid, Chip, Divider,
} from '@mui/material'
import {
  ExpandMore, ExpandLess, Fingerprint, CompareArrows, RuleFolder, EventRepeat,
} from '@mui/icons-material'
import DriftBadge from './DriftBadge'

/**
 * Explains what a certificate is, how it is produced, and what to do about one.
 *
 * Users were reading the Certificates page without a model of what it was for:
 * the only framing was a subtitle saying certificates "track drift and validate
 * governance", which names the machinery rather than the job it does.
 *
 * Accuracy notes, because this copy is the product's claim about itself:
 *
 *  - Certificates are NOT cryptographically signed. They are records in ARIAS.
 *    docs/FEATURE_ROADMAP.md describes "signed certificates"; that is wrong and
 *    this component must not repeat it. Signing is deferred and demand-gated
 *    (CONTINUOUS_ATTESTATION_PLAN §6).
 *  - A certificate says an agent still behaves as it did when certified. It does
 *    NOT say the agent is safe, compliant, or reviewed by anyone.
 *  - Blocking a release is opt-in. Most tenants run monitor mode, where a
 *    certificate reports and never stops a pipeline.
 */

const STEPS = [
  {
    icon: <Fingerprint fontSize="small" />,
    title: 'A scan fingerprints each agent',
    body: 'When your scanner finds an agent, ARIAS records six things about how it behaves: its goal, the tools it can call, what it reads and writes to memory, how it delegates, how it handles errors, and which model it runs on.',
  },
  {
    icon: <RuleFolder fontSize="small" />,
    title: 'The first fingerprint becomes its certified baseline',
    body: 'That baseline is what "normal" means for this agent. It is issued automatically — there is nothing to request or configure.',
  },
  {
    icon: <CompareArrows fontSize="small" />,
    title: 'Every later scan is compared against that baseline',
    body: 'Not against the previous scan. That distinction matters: a change that looks trivial three times running can add up to a material one, and comparing against the baseline is what catches it.',
  },
  {
    icon: <EventRepeat fontSize="small" />,
    title: 'You re-attest when a change is intended',
    body: 'Approve accepts a specific change and makes it the new baseline. Renew does the same and restarts the 90-day validity period. Both are recorded with your name and reason.',
  },
]

const VERDICT_ACTIONS: Array<{ verdict: string; action: string }> = [
  { verdict: 'STABLE', action: 'Nothing to do.' },
  { verdict: 'POSITIVE_DRIFT', action: 'Nothing to do — worth noticing that it improved.' },
  { verdict: 'COSMETIC_DRIFT', action: 'Glance at it. No capability changed.' },
  { verdict: 'MATERIAL_DRIFT', action: 'Decide: was this intended? Approve it, or fix the code and re-scan.' },
  { verdict: 'CRITICAL_DRIFT', action: 'Review before shipping. This is the class of change worth a second pair of eyes.' },
  { verdict: 'REBASELINE', action: 'Nothing to do. This one is us, not you.' },
]

const CertificateGuide = () => {
  // Collapsed by default: the people who need it read it once, and the people
  // who do not should not have to scroll past it every visit.
  const [open, setOpen] = useState(false)

  return (
    <Card sx={{ mb: 3 }} variant="outlined">
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: open ? 2 : 1.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              What a certificate tells you
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Each agent gets one. It records how the agent behaved when you certified
              it, and every scan since is measured against that — so you find out when
              an agent starts doing something it did not used to do.
            </Typography>
          </Box>
          <Button
            size="small"
            onClick={() => setOpen(!open)}
            endIcon={open ? <ExpandLess /> : <ExpandMore />}
            sx={{ flexShrink: 0, textTransform: 'none' }}
          >
            {open ? 'Less' : 'How it works'}
          </Button>
        </Box>

        <Collapse in={open} timeout="auto">
          <Divider sx={{ my: 2 }} />

          <Grid container spacing={2}>
            {STEPS.map((s, i) => (
              <Grid item xs={12} md={6} key={s.title}>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <Box sx={{ color: 'primary.main', mt: 0.25 }}>{s.icon}</Box>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {i + 1}. {s.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {s.body}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            What each verdict means for your day
          </Typography>
          <Grid container spacing={1}>
            {VERDICT_ACTIONS.map(({ verdict, action }) => (
              <Grid item xs={12} key={verdict}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ minWidth: 132 }}><DriftBadge verdict={verdict} /></Box>
                  <Typography variant="body2" color="text.secondary">{action}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Does this block my release?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Only if you ask it to. The CI snippet ARIAS generates runs in monitor
                mode by default — it reports drift and never fails the build. Switching
                to block mode is a one-line change in your pipeline file, and you choose
                which verdicts stop a release and which agents are in scope.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                What a certificate does not claim
              </Typography>
              <Typography variant="body2" color="text.secondary">
                It is a record in ARIAS, not a cryptographic signature, and it is not a
                statement that the agent is safe or compliant. It says one thing
                precisely: this agent still behaves the way it did when you certified
                it, or here is exactly what changed.
              </Typography>
            </Grid>
          </Grid>

          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Chip size="small" label="Valid for 90 days" variant="outlined" />
            <Typography variant="caption" color="text.secondary">
              You are warned 14 days before one expires. An expired certificate fails the
              CI gate the same way a drifted one does, so renew before it lapses.
            </Typography>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  )
}

export default CertificateGuide
